import { useState, useEffect } from 'react';
import { MessageSquare, Clock, CheckCircle, AlertCircle, Mail, Phone, User, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  'Pendiente': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  'En proceso': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Resuelto': 'bg-green-500/10 text-green-400 border-green-500/20'
};

const STATUS_ICONS = {
  'Pendiente': Clock,
  'En proceso': AlertCircle,
  'Resuelto': CheckCircle
};

export function AdminContactMessages() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [filter]);

  const fetchMessages = async () => {
    try {
      const url = filter ? `${API}/admin/contact-messages?status=${filter}` : `${API}/admin/contact-messages`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (messageId, newStatus) => {
    setUpdating(true);
    try {
      const res = await fetch(`${API}/admin/contact-messages/${messageId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, status: newStatus } : m
        ));
        if (selectedMessage?.id === messageId) {
          setSelectedMessage(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = messages.filter(m => m.status === 'Pendiente').length;
  const inProcessCount = messages.filter(m => m.status === 'En proceso').length;
  const resolvedCount = messages.filter(m => m.status === 'Resuelto').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-contact-messages">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-manrope font-bold text-white">Mensajes de Contacto</h1>
          <p className="text-gray-400 mt-1">Gestiona los mensajes recibidos del formulario de contacto</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          onClick={() => setFilter(filter === 'Pendiente' ? '' : 'Pendiente')}
          className={`p-4 rounded-xl border transition-colors ${
            filter === 'Pendiente' 
              ? 'bg-yellow-500/20 border-yellow-500' 
              : 'bg-[#111111] border-[#2a2a2a] hover:border-[#3a3a3a]'
          }`}
        >
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-yellow-400" />
            <div className="text-left">
              <p className="text-2xl font-bold text-white">{pendingCount}</p>
              <p className="text-sm text-gray-400">Pendientes</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => setFilter(filter === 'En proceso' ? '' : 'En proceso')}
          className={`p-4 rounded-xl border transition-colors ${
            filter === 'En proceso' 
              ? 'bg-blue-500/20 border-blue-500' 
              : 'bg-[#111111] border-[#2a2a2a] hover:border-[#3a3a3a]'
          }`}
        >
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400" />
            <div className="text-left">
              <p className="text-2xl font-bold text-white">{inProcessCount}</p>
              <p className="text-sm text-gray-400">En proceso</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => setFilter(filter === 'Resuelto' ? '' : 'Resuelto')}
          className={`p-4 rounded-xl border transition-colors ${
            filter === 'Resuelto' 
              ? 'bg-green-500/20 border-green-500' 
              : 'bg-[#111111] border-[#2a2a2a] hover:border-[#3a3a3a]'
          }`}
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <div className="text-left">
              <p className="text-2xl font-bold text-white">{resolvedCount}</p>
              <p className="text-sm text-gray-400">Resueltos</p>
            </div>
          </div>
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Message List */}
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#2a2a2a]">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-500" />
              Mensajes ({messages.length})
            </h2>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {messages.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No hay mensajes {filter ? `con estado "${filter}"` : ''}</p>
              </div>
            ) : (
              <div className="divide-y divide-[#2a2a2a]">
                {messages.map((message) => {
                  const StatusIcon = STATUS_ICONS[message.status] || Clock;
                  return (
                    <button
                      key={message.id}
                      onClick={() => setSelectedMessage(message)}
                      className={`w-full p-4 text-left hover:bg-[#1a1a1a] transition-colors ${
                        selectedMessage?.id === message.id ? 'bg-[#1a1a1a]' : ''
                      }`}
                      data-testid={`message-item-${message.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white truncate">{message.nombre}</span>
                            <Badge variant="outline" className={`text-xs ${STATUS_COLORS[message.status]}`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {message.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-amber-400 mb-1">{message.motivo}</p>
                          <p className="text-sm text-gray-400 line-clamp-2">{message.mensaje}</p>
                          <p className="text-xs text-gray-500 mt-2">{formatDate(message.created_at)}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Message Detail */}
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#2a2a2a]">
            <h2 className="font-semibold text-white">Detalle del Mensaje</h2>
          </div>
          {selectedMessage ? (
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#1a1a1a] rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Nombre</p>
                    <p className="text-white font-medium">{selectedMessage.nombre}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#1a1a1a] rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Correo</p>
                    <a href={`mailto:${selectedMessage.email}`} className="text-amber-400 hover:underline">
                      {selectedMessage.email}
                    </a>
                  </div>
                </div>
                {selectedMessage.telefono && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#1a1a1a] rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Teléfono</p>
                      <a href={`tel:${selectedMessage.telefono}`} className="text-white">
                        {selectedMessage.telefono}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Motivo */}
              <div className="bg-[#1a1a1a] rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-1">Motivo</p>
                <p className="text-amber-400 font-medium">{selectedMessage.motivo}</p>
              </div>

              {/* Message */}
              <div>
                <p className="text-sm text-gray-400 mb-2">Mensaje</p>
                <div className="bg-[#1a1a1a] rounded-lg p-4">
                  <p className="text-white whitespace-pre-wrap">{selectedMessage.mensaje}</p>
                </div>
              </div>

              {/* Date */}
              <p className="text-sm text-gray-500">
                Recibido: {formatDate(selectedMessage.created_at)}
              </p>

              {/* Status Actions */}
              <div className="pt-4 border-t border-[#2a2a2a]">
                <p className="text-sm text-gray-400 mb-3">Cambiar estado</p>
                <div className="flex flex-wrap gap-2">
                  {['Pendiente', 'En proceso', 'Resuelto'].map((status) => (
                    <Button
                      key={status}
                      variant={selectedMessage.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateStatus(selectedMessage.id, status)}
                      disabled={updating || selectedMessage.status === status}
                      className={
                        selectedMessage.status === status
                          ? 'bg-amber-500 text-black hover:bg-amber-600'
                          : 'border-[#2a2a2a] text-gray-300 hover:bg-[#1a1a1a]'
                      }
                      data-testid={`status-btn-${status.toLowerCase().replace(' ', '-')}`}
                    >
                      {updating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                      {status}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Selecciona un mensaje para ver los detalles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminContactMessages;
